import { useState, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { dashboard } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { formatCount } from '@cwos/ui-shared';
import type { Severity } from '@cwos/ui-shared';
import { Pill, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import {
  alertSeverityTone,
  clampExpectedHours,
  docStateLabel,
  docStateTone,
  exceptionTrendLabel,
  exceptionTrendTone,
  gapRatioLabel,
  gapSeverityLabel,
  gapSeverityTone,
  hoursLabel,
  percentLabel,
  reconciliationItemLabel,
  reconciliationItemTone,
  serviceTrendLabel,
  serviceTrendTone,
  type DashboardPeriod,
} from './dashboardLogic.js';
import { isSmallCohortSuppressed, SUPPRESSED_LABEL } from './analytics/disclosure.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { OWN_CASELOAD } from '../delivery.js';
import {
  CARE_GAP_CASELOAD,
  EXCEPTION_CASELOAD,
  OPEN_PAPERWORK_CASELOAD,
  queueScopeNote,
} from './queueScope.js';

type ServiceLevelSummary = DashboardNS.ServiceLevelSummary;
type WorkerServiceLevel = DashboardNS.WorkerServiceLevel;
type CareGapSummary = DashboardNS.CareGapSummary;
type ClientCareGap = DashboardNS.ClientCareGap;
type DocumentationSummary = DashboardNS.DocumentationSummary;
type OpenDocument = DashboardNS.OpenDocument;
type ReconciliationSummary = DashboardNS.ReconciliationSummary;
type ReconciliationItem = DashboardNS.ReconciliationItem;
type ExceptionSummary = DashboardNS.ExceptionSummary;
type ExceptionDetail = DashboardNS.ExceptionDetail;

// ---------------------------------------------------------------------------
// Shared furniture
// ---------------------------------------------------------------------------

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="db-stat">
      <div className="db-stat-value">{value}</div>
      <div className="db-stat-label">{label}</div>
    </div>
  );
}

function StatGrid({ children }: { children: ReactNode }) {
  return <div className="db-stats">{children}</div>;
}

function TrendLine({ label, tone }: { label: string; tone: Severity }) {
  return (
    <p
      className="rn-muted"
      style={{
        margin: 'var(--space-10) 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-8)',
      }}
    >
      <Pill variant={tone} dot>
        {label}
      </Pill>
    </p>
  );
}

interface WidgetCardProps {
  title: string;
  toneLabel?: string;
  tone?: Severity;
  open: boolean;
  onToggle: () => void;
  summary: ReactNode;
  /** Null: there is nothing to drill into, so no "View details" is offered. */
  drilldown: ReactNode;
}

function WidgetCard({
  title,
  toneLabel,
  tone,
  open,
  onToggle,
  summary,
  drilldown,
}: WidgetCardProps) {
  return (
    <section className="rn-card">
      <div className="rn-card-head">
        <h2 className="rn-card-title">{title}</h2>
        {toneLabel ? (
          <Pill variant={tone ?? 'plain'} dot>
            {toneLabel}
          </Pill>
        ) : null}
      </div>
      {summary}
      {drilldown == null ? null : (
        <>
          <div style={{ marginTop: 'var(--space-12)' }}>
            <button type="button" className="rn-link" aria-expanded={open} onClick={onToggle}>
              {open ? 'Hide details' : 'View details →'}
            </button>
          </div>
          {open ? <div style={{ marginTop: 'var(--space-12)' }}>{drilldown}</div> : null}
        </>
      )}
    </section>
  );
}

/**
 * Every widget's drill-down, and the reason all five got the same four bugs.
 *
 * The chain it replaces was `isLoading → isError && !data → (data ?? []).length
 * === 0 → SuccessEmpty`, which is four states rendered as three:
 *
 *   - a 403 took the `isError` branch and got "Try again", a button offering to
 *     ask a question that has already been answered;
 *   - every one of these queries is `enabled: open`, and react-query reports a
 *     disabled query as neither loading nor errored with `data === undefined`,
 *     so the FIRST render of an opened drawer fell through `?? []` into the
 *     empty state — and the empty copy on these five widgets is "Everything
 *     lines up", "No active alerts", "Every client with a plan is on track
 *     right now". Three safety claims, made before the request went out;
 *   - a failed refresh over rows that had loaded blanked them;
 *   - a failed refresh over a retained EMPTY answer had no state at all.
 *
 * `listPhase` decides all four in one place, and `ListState` will not compile
 * without a failure sentence beside the empty one.
 */
function DrilldownTable<Row>({
  query,
  columns,
  rowKey,
  emptyHeading,
  emptyMessage,
  onRowOpen,
}: {
  query: UseQueryResult<Row[], Error>;
  columns: Column<Row>[];
  rowKey: (row: Row) => string;
  emptyHeading?: string;
  emptyMessage?: string;
  onRowOpen?: (row: Row) => void;
}) {
  const rows = query.data ?? [];
  const phase = listPhase(query, query.data === undefined ? undefined : rows.length);
  return (
    <ListState
      phase={phase}
      surface="table"
      rows={4}
      failedMessage="We couldn’t load these details just now, so this is not a count of none."
      onRetry={() => void query.refetch()}
      empty={
        <SuccessEmpty
          heading={emptyHeading ?? 'Nothing here'}
          message={emptyMessage ?? 'No rows match right now.'}
        />
      }
    >
      <TableCard columns={columns} rows={rows} rowKey={rowKey} onRowOpen={onRowOpen} />
    </ListState>
  );
}

// ---------------------------------------------------------------------------
// 1 · Service Levels
// ---------------------------------------------------------------------------

export function ServiceLevelsWidget({
  period,
  orgBenchmarkHours,
}: {
  period: DashboardPeriod;
  /**
   * A UNIFORM ORGANIZATION BAR the reader chose, or `null` for none. It is NOT
   * anybody's plan: every per-worker figure in the drawer below is measured
   * against that worker's own contracted hours, and this number never reaches
   * the drill-down query at all.
   */
  orgBenchmarkHours: number | null;
}) {
  const [open, setOpen] = useState(false);
  const bar = orgBenchmarkHours == null ? undefined : clampExpectedHours(orgBenchmarkHours);

  const summary = useQuery({
    queryKey: ['dashboard', 'service-levels', period, bar ?? 'no-benchmark'],
    queryFn: () => dashboard.serviceLevels({ period, orgBenchmarkWeeklyHours: bar }),
    refetchInterval: 30_000,
  });

  const details = useQuery({
    queryKey: ['dashboard', 'service-levels', 'workers'],
    queryFn: () => dashboard.serviceLevelWorkers(),
    enabled: open,
  });

  /**
   * The trend, ONLY when it is a movement this reader may be told about.
   *
   * Three different reasons to show no pill, collapsed here on purpose because
   * the PILL has one absent state and the BODY carries the distinctions: no
   * payload yet, a roster of nobody (`population: 'none'`), and a roster below
   * the cohort floor. `?.` on scope in both gates is load-bearing (F161) — the
   * api-client casts rather than parses, so `scope` can genuinely be absent.
   */
  const d0 = summary.data;
  const trend =
    d0 &&
    d0.scope?.population !== undefined &&
    d0.scope.population !== 'none' &&
    d0.trendDirection !== 'suppressed_small_cohort'
      ? d0.trendDirection
      : undefined;

  return (
    <WidgetCard
      title="Service levels"
      // The tone pill speaks for the roster, so a roster of nobody gets no tone. The
      // numbers below already say why through the scope note; a 'Steady' pill over
      // them would be the card's headline contradicting its own caption.
      // `?.` on scope, in BOTH gates (F161): the api-client casts this payload
      // rather than parsing it, so `scope` can genuinely be absent — and a
      // direct `.population` read here crashed the whole card before
      // `queueScopeNote`'s unstated-frame arm could render anything. A missing
      // frame withholds the tone pill the same way `none` does: the pill
      // speaks for a roster, and we cannot say whose roster this is.
      // A SUPPRESSED TREND GETS NO PILL EITHER, for the same reason a `none`
      // roster does not: the pill speaks for a movement, and over a roster
      // below the cohort floor "Improving" IS one worker's week compared with
      // her last one. `serviceTrendLabel`/`Tone` are deliberately NOT widened
      // to accept the state — they are shared with the exceptions widget, which
      // has no cohort and can never be in it, and a fifth label there would be
      // dead copy. The card's body says the figures are hidden and why.
      toneLabel={trend ? serviceTrendLabel(trend) : undefined}
      tone={trend ? serviceTrendTone(trend) : undefined}
      open={open}
      onToggle={() => setOpen((v) => !v)}
      summary={<ServiceLevelSummaryBody query={summary} />}
      drilldown={<WorkerDrilldown period={period} query={details} />}
    />
  );
}

/**
 * A summary card's body, in the four states it can actually be in.
 *
 * All five widgets hand-rolled `isLoading → isError && !data → if (!d) return
 * null`, which answers a 403 with a retry button and answers "we have not asked
 * yet" with an EMPTY CARD BODY — a heading, a "View details" link, and no
 * numbers, which reads as a widget that has nothing to report rather than one
 * that has not been told.
 *
 * `render` is only ever called with a payload, so a summary body cannot be
 * written that reaches a `?? 0`.
 */
function SummaryBody<T>({
  query,
  failedMessage,
  render,
}: {
  query: UseQueryResult<T, Error>;
  failedMessage: string;
  render: (data: T) => ReactNode;
}) {
  return (
    <ListState
      phase={listPhase(query)}
      surface="table"
      rows={2}
      // Not a list: `listPhase` with no row count never answers `empty`.
      empty={null}
      failedMessage={failedMessage}
      onRetry={() => void query.refetch()}
    >
      {query.data !== undefined ? render(query.data) : null}
    </ListState>
  );
}

/**
 * The frame, ABOVE the numbers it qualifies.
 *
 * Placement is the whole of it: a reader who has already read "Open 4" and
 * moved on has been misinformed, and a caption they reach second cannot undo
 * that. Same position as the frame over TimeClock's four counts.
 *
 * One component rather than the same six lines on five cards, because the five
 * cards were drifting apart on exactly the thing they must agree on. `null` —
 * an organization-wide answer, which needs no defending — renders nothing, so a
 * caller cannot forget the branch and put a note on every card.
 */
function ScopeNote({ note }: { note: string | null }) {
  if (note === null) return null;
  return (
    <p className="rn-muted" role="note" style={{ margin: '0 0 var(--space-6)' }}>
      {note}
    </p>
  );
}

/**
 * WHOSE ROSTER `totalWorkers` AND `belowThreshold` COUNT.
 *
 * `ServiceLevelSummary.scope` is the same `QueueScope` the paperwork and
 * reconciliation cards below already read, and this card read three figures and
 * not the frame. `GET /api/dashboard/service-levels` counts over the roster the
 * reader's `program:read` grants reach, so "48 staff, 6 below plan" was a true
 * statement about two programs set in the same typeface as an organization's.
 *
 * `population: 'none'` IS LIVE HERE, and it is the case that mattered: a
 * SITE-scoped reader cannot be expressed on this view and is failed closed, so
 * the payload arrives as `totalWorkers: 0, belowThreshold: 0` — a roster with
 * nobody on it, which reads as "everyone is on plan" and is the reassuring
 * direction. The server's own sentence now sits above those zeros.
 *
 * THE CASELOAD NOUN IS `OWN_CASELOAD` and never renders today: the payload
 * documents `caseload` as always false, because the scoped roster comes from
 * the program assignment relation and not from the supervisor edge. It is named
 * anyway because `queueScopeNote` is a total function over a type the server
 * owns, and the day that edge does reach this view the sentence must be the one
 * the delivery band and the reconciliation card already use for it — not a
 * fourth description of one population, invented in a hurry.
 */
function ServiceLevelSummaryBody({ query }: { query: UseQueryResult<ServiceLevelSummary, Error> }) {
  return (
    <SummaryBody
      query={query}
      failedMessage="We couldn’t load service levels just now, so these are not zeros."
      render={(d) => (
        <>
          <ScopeNote note={queueScopeNote(d.scope, OWN_CASELOAD)} />
          {/*
            TWO NOTES, NEVER ONE, BECAUSE THEY ARE TWO DIFFERENT FACTS.
            `ScopeNote` above says whose roster this is — an AUTHORIZATION
            frame, fixed by a grant. This one says how many of that roster have
            no contracted hours recorded — a DATA gap, fixed by somebody typing
            them in, and identical for every reader. Merging them would tell a
            supervisor she lacks access to a number nobody has ever recorded.
          */}
          {d.workersWithoutExpectedHours > 0 ? (
            <ScopeNote
              note={`${d.workersWithoutExpectedHours} of these ${d.totalWorkers} have no contracted weekly hours on file, so they are not counted below. This is missing data, not a permission.`}
            />
          ) : null}
          <StatGrid>
            <Stat label="Staff" value={formatCount(d.totalWorkers)} />
            {/*
              `null` IS NOT ZERO AND IS NOT A DASH. `belowThreshold` arrives
              null only when the cohort floor removed it, and
              `disclosure.suppression` names it; rendering `String(null)` put
              the literal text "null" on the card, and rendering `0` would have
              said every worker is on plan. The word, not a glyph — see
              SUPPRESSED_LABEL.
            */}
            <Stat
              label="Below plan"
              value={d.belowThreshold == null ? SUPPRESSED_LABEL : String(d.belowThreshold)}
            />
            {/*
              "Hours vs plan", NOT "Avg of plan". The server now sends a ratio
              of sums — delivered hours over contracted hours — where it used to
              send the mean of the per-worker percentages. They are different
              statistics that diverge as soon as contracts are unequal, so the
              label changed with the field rather than after somebody noticed.
            */}
            {/*
              THREE REASONS FOR NO NUMBER, AND THE TILE SHOWS TWO DIFFERENT
              THINGS. `not_calculable` keeps the em dash `percentLabel(null)`
              renders, because nothing was withheld — nobody has a contract on
              file and every reader sees the same blank. Suppression shows the
              WORD, because a figure exists and is being held back, and a dash
              would make the two indistinguishable on a card that shows them in
              the same slot. Both get a sentence underneath; neither borrows the
              other's.
            */}
            <Stat
              label="Hours vs plan"
              value={
                d.utilization.kind === 'ratio_of_sums'
                  ? percentLabel(d.utilization.percent)
                  : isSmallCohortSuppressed(d.utilization)
                    ? SUPPRESSED_LABEL
                    : percentLabel(null)
              }
            />
            {/*
              `orgBenchmark: null` STILL MEANS "no benchmark was requested" and
              renders no tile at all. The suppressed variant is a different
              thing — the caller DID send a bar and we will not price it over a
              roster this small — so it keeps its tile and says so. Collapsing
              them would tell the caller their parameter was ignored.
            */}
            {d.orgBenchmark == null ? null : isSmallCohortSuppressed(d.orgBenchmark) ? (
              <Stat label="vs your benchmark" value={SUPPRESSED_LABEL} />
            ) : (
              <Stat
                label={`vs ${d.orgBenchmark.weeklyHours}h benchmark`}
                value={percentLabel(d.orgBenchmark.percent)}
              />
            )}
          </StatGrid>
          {/*
            THE REASON, NOT A DASH. `percentLabel(null)` renders an em dash,
            which reads as a rendering gap; the server ships a sentence for this
            exact reason and the screen renders it rather than inventing a
            second wording.
          */}
          {d.utilization.kind === 'not_calculable' ? (
            <ScopeNote note={d.utilization.message} />
          ) : null}
          {/*
            THE SUPPRESSION SENTENCE, ONCE, FROM THE SERVER. It covers every
            hidden figure on the card rather than repeating under each tile, and
            it is the server's wording rather than a second one invented here —
            the same discipline the `not_calculable` note above follows.
            Deliberately a SEPARATE branch from that one: they are different
            states with different remedies, and a reader must not have to work
            out which blank they are looking at.
          */}
          {isSmallCohortSuppressed(d.utilization) ? (
            <ScopeNote note={d.utilization.message} />
          ) : isSmallCohortSuppressed(d.orgBenchmark) ? (
            <ScopeNote note={d.orgBenchmark.message} />
          ) : null}
        </>
      )}
    />
  );
}

/**
 * THE PER-WORKER DRAWER, WHERE THE DENOMINATOR IS THE WORKER'S OWN CONTRACT.
 *
 * Every row's `expectedHours*`/`percentOfExpected*` is now `number | null`, and
 * `null` is not a gap to paper over: it means this worker has no contracted
 * weekly hours recorded, so there is no plan for her delivery to be a
 * percentage of. The server sends the sentence; the table renders it in the
 * cell rather than a `0` (an accusation) or a bare dash (a rendering gap).
 */
function WorkerDrilldown({
  period,
  query,
}: {
  period: DashboardPeriod;
  query: UseQueryResult<WorkerServiceLevel[], Error>;
}) {
  const is7d = period === '7d';
  const columns: Column<WorkerServiceLevel>[] = [
    {
      key: 'worker',
      header: 'Worker',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.workerLabel}</span>
          {r.programName ? <span className="rn-muted"> · {r.programName}</span> : null}
        </span>
      ),
    },
    {
      key: 'actual',
      header: 'Actual',
      render: (r) => hoursLabel(is7d ? r.actualHours7d : r.actualHours30d),
      numeric: true,
    },
    {
      key: 'expected',
      header: 'Plan',
      render: (r) => {
        const expected = is7d ? r.expectedHours7d : r.expectedHours30d;
        return expected == null ? (
          <span
            className="rn-muted"
            title={r.denominator.kind === 'not_calculable' ? r.denominator.message : undefined}
          >
            No plan on file
          </span>
        ) : (
          hoursLabel(expected)
        );
      },
      numeric: true,
    },
    {
      key: 'pct',
      header: '% of plan',
      render: (r) => {
        const pct = is7d ? r.percentOfExpected7d : r.percentOfExpected30d;
        return pct == null ? <span className="rn-muted">—</span> : percentLabel(pct);
      },
      numeric: true,
    },
    {
      key: 'trend',
      header: 'Trend',
      render: (r) => (
        <Pill variant={serviceTrendTone(r.trendDirection)} dot>
          {serviceTrendLabel(r.trendDirection)}
        </Pill>
      ),
    },
  ];
  return (
    <DrilldownTable
      query={query}
      columns={columns}
      rowKey={(r) => r.workerId}
      emptyHeading="No staff hours yet"
      emptyMessage="Hours appear once workers clock in."
    />
  );
}

// ---------------------------------------------------------------------------
// 2 · Care Gaps
// ---------------------------------------------------------------------------

export function CareGapsWidget({ period }: { period: DashboardPeriod }) {
  const [open, setOpen] = useState(false);

  const summary = useQuery({
    queryKey: ['dashboard', 'care-gaps', period],
    queryFn: () => dashboard.careGaps({ period }),
  });

  const details = useQuery({
    queryKey: ['dashboard', 'care-gaps', 'clients', period],
    queryFn: () => dashboard.careGapDetails({ period }),
    enabled: open,
  });

  return (
    <WidgetCard
      title="Care gaps"
      open={open}
      onToggle={() => setOpen((v) => !v)}
      summary={<CareGapSummaryBody query={summary} />}
      drilldown={<ClientDrilldown query={details} />}
    />
  );
}

/**
 * WHICH CLIENTS `clientsWithGaps` COUNTS.
 *
 * Narrowed by the reader's `program:read` grants, and this card rendered the
 * five counts bare. `clientsWithGaps: 0` under `population: 'none'` is the
 * sentence NOBODY IS BEHIND ON CARE, made out of a client list that was never
 * searched — the same manufactured all-clear the reconciliation cells refuse,
 * on the card a reader is more likely to believe because it is not an alarm.
 *
 * THE CASELOAD NOUN NAMES CLIENTS, not workers — see `CARE_GAP_CASELOAD` in
 * `queueScope.ts`, which the landing screen's care-gap tile reads too.
 */
function CareGapSummaryBody({ query }: { query: UseQueryResult<CareGapSummary, Error> }) {
  return (
    <SummaryBody
      query={query}
      failedMessage="We couldn’t load care gaps just now, so these are not zeros."
      render={(d) => (
        <>
          <ScopeNote note={queueScopeNote(d.scope, CARE_GAP_CASELOAD)} />
          <StatGrid>
            <Stat label="With a plan" value={formatCount(d.totalClientsWithTargets)} />
            <Stat label="With gaps" value={String(d.clientsWithGaps)} />
            <Stat label="Severe" value={String(d.gapCountBySeverity.severe)} />
            <Stat label="Moderate" value={String(d.gapCountBySeverity.moderate)} />
            <Stat label="Minor" value={String(d.gapCountBySeverity.minor)} />
          </StatGrid>
        </>
      )}
    />
  );
}

function ClientDrilldown({ query }: { query: UseQueryResult<ClientCareGap[], Error> }) {
  const columns: Column<ClientCareGap>[] = [
    {
      key: 'client',
      header: 'Client',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.clientLabel}</span>
          {r.serviceTypeName ? <span className="rn-muted"> · {r.serviceTypeName}</span> : null}
        </span>
      ),
    },
    {
      key: 'severity',
      header: 'Gap',
      render: (r) => (
        <Pill variant={gapSeverityTone(r.severity)} dot>
          {gapSeverityLabel(r.severity)}
        </Pill>
      ),
    },
    {
      key: 'delivered',
      header: 'Delivered',
      render: (r) => String(r.actualUnits),
      numeric: true,
    },
    {
      key: 'target',
      header: 'Target',
      render: (r) => String(r.targetUnits),
      numeric: true,
    },
    {
      key: 'ratio',
      header: 'Of plan',
      render: (r) => gapRatioLabel(r.gapRatio),
      numeric: true,
    },
  ];
  return (
    <DrilldownTable
      query={query}
      columns={columns}
      rowKey={(r) => r.clientId}
      emptyHeading="No care gaps"
      emptyMessage="Every client with a plan is on track right now."
    />
  );
}

// ---------------------------------------------------------------------------
// 3 · Documentation
// ---------------------------------------------------------------------------

export function DocumentationWidget() {
  const [open, setOpen] = useState(false);

  const summary = useQuery({
    queryKey: ['dashboard', 'documentation'],
    queryFn: () => dashboard.documentation(),
  });

  const details = useQuery({
    queryKey: ['dashboard', 'documentation', 'open'],
    queryFn: () => dashboard.documentationDetails(),
    enabled: open,
  });

  return (
    <WidgetCard
      title="Documentation"
      open={open}
      onToggle={() => setOpen((v) => !v)}
      summary={<DocumentationSummaryBody query={summary} />}
      drilldown={<DocumentDrilldown query={details} />}
    />
  );
}

/**
 * WHOSE OPEN PAPERWORK THESE FIVE NUMBERS ARE.
 *
 * `DocumentationSummary.scope` has been on the wire since the queue grew its
 * frame, and this widget read `totalOpen` and nothing else. A supervisor whose
 * documentation read is narrowed to their own reports was therefore shown
 * "Open 4" in the same typeface an org-wide admin sees "Open 4" — the plain
 * inversion `QueueScope` exists to prevent, on the landing screen's paperwork
 * figure. The card's own `failedMessage` already refuses to let an unanswered
 * request read as zero; this refuses to let a NARROWED answer read as the
 * organization's.
 *
 * The three arms live in `queueScope.ts`, shared with the four other cards on
 * this screen, with the configurable dashboard's table widgets and now with the
 * landing screen's question tiles — one `QueueScope`, one reading of it. The
 * NOUN lives there too, as `OPEN_PAPERWORK_CASELOAD`: this card counts
 * paperwork and the card beside it counts something else, but the tile that
 * reads THE SAME PAYLOAD has to say the same word this card does.
 */
function DocumentationSummaryBody({
  query,
}: {
  query: UseQueryResult<DocumentationSummary, Error>;
}) {
  return (
    <SummaryBody
      query={query}
      failedMessage="We couldn’t load documentation status just now, so these are not zeros."
      render={(d) => {
        return (
          <>
            <ScopeNote note={queueScopeNote(d.scope, OPEN_PAPERWORK_CASELOAD)} />
            <StatGrid>
              <Stat label="Open" value={formatCount(d.totalOpen)} />
              <Stat label="< 24 h" value={String(d.byAge.under24h)} />
              <Stat label="1–3 days" value={String(d.byAge.day1to3)} />
              <Stat label="4–7 days" value={String(d.byAge.day4to7)} />
              <Stat label="> 7 days" value={String(d.byAge.over7d)} />
            </StatGrid>
          </>
        );
      }}
    />
  );
}

function DocumentDrilldown({ query }: { query: UseQueryResult<OpenDocument[], Error> }) {
  const columns: Column<OpenDocument>[] = [
    {
      key: 'client',
      header: 'Client',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.clientLabel}</span>
          {r.workerLabel ? <span className="rn-muted"> · {r.workerLabel}</span> : null}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'State',
      render: (r) => (
        <Pill variant={docStateTone(r.isLate)} dot>
          {docStateLabel(r.isLate)}
        </Pill>
      ),
    },
    {
      key: 'age',
      header: 'Open for',
      render: (r) => (r.lagDays !== null ? `${r.lagDays}d` : '—'),
      numeric: true,
    },
    {
      key: 'incomplete',
      header: 'Incomplete',
      render: (r) => String(r.incompleteCount),
      numeric: true,
    },
  ];
  return (
    <DrilldownTable
      query={query}
      columns={columns}
      rowKey={(r) => r.docStatusId}
      emptyHeading="Nothing open"
      emptyMessage="Documentation is caught up."
    />
  );
}

// ---------------------------------------------------------------------------
// 4 · Reconciliation
// ---------------------------------------------------------------------------

export function ReconciliationWidget({ period }: { period: DashboardPeriod }) {
  const [open, setOpen] = useState(false);

  const summary = useQuery({
    queryKey: ['dashboard', 'reconciliation', period],
    queryFn: () => dashboard.reconciliation({ period }),
    refetchInterval: 30_000,
  });

  // No time clock: the drill-down's empty answer would read "Everything lines
  // up" under "No time clock connected" — an all-clear about nothing measured.
  const noSource = summary.data?.unavailableReason === 'no_source';
  const details = useQuery({
    queryKey: ['dashboard', 'reconciliation', 'items', period],
    queryFn: () => dashboard.reconciliationDetails({ period }),
    enabled: open && !noSource,
  });

  return (
    <WidgetCard
      title="Reconciliation"
      open={open}
      onToggle={() => setOpen((v) => !v)}
      summary={<ReconciliationSummaryBody query={summary} />}
      drilldown={noSource ? null : <ReconciliationDrilldown query={details} />}
    />
  );
}

/**
 * WHOSE UNMATCHED WORK THESE FOUR NUMBERS ARE.
 *
 * The SAME field, on the SAME type, one card over.
 * `ReconciliationSummary.scope` is a `QueueScope` exactly as
 * `DocumentationSummary.scope` is, and `GET /api/dashboard/reconciliation`
 * narrows by the reader's `program:read` grants and by the supervisor edge
 * unless they hold the org-wide read keys for the two tables it mixes. This
 * card read the four counts and not the frame, so a supervisor's "Total 3" was
 * set in the same typeface as an org-wide admin's "Total 3".
 *
 * THE CASELOAD NOUN IS `OWN_CASELOAD` and not a phrase written for this card.
 * The edge the server applied is `worker-scope.ts`'s — the reader's own worker
 * row plus their direct reports — and `delivery.ts` already exports the
 * sentence this product uses for it, precisely so that the landing screen's
 * delivery band and this card cannot drift into two descriptions of one
 * population. The documentation card says something different because it counts
 * paperwork rather than people; this one counts visits and time records against
 * the workers who logged them, so it names the workers.
 */
function ReconciliationSummaryBody({
  query,
}: {
  query: UseQueryResult<ReconciliationSummary, Error>;
}) {
  return (
    <SummaryBody
      query={query}
      failedMessage="We couldn’t load reconciliation just now, so these are not zeros."
      render={(d) => {
        // No time clock: not measured, never four zeros (owner ruling 2026-10-07).
        if (d.unavailableReason === 'no_source' || d.totalReconciliationItems === null) {
          return (
            <p className="rn-muted">
              No time clock connected — visits are not reconciled against clock-ins for this
              organization.
            </p>
          );
        }
        return (
          <>
            <ScopeNote note={queueScopeNote(d.scope, OWN_CASELOAD)} />
            <StatGrid>
              <Stat label="Visit w/o time" value={String(d.eventsWithoutSession)} />
              <Stat label="Time w/o visit" value={String(d.sessionsWithoutEvent)} />
              <Stat label="Minute mismatches" value={String(d.minuteMismatches)} />
              <Stat label="Total" value={formatCount(d.totalReconciliationItems)} />
            </StatGrid>
          </>
        );
      }}
    />
  );
}

function ReconciliationDrilldown({
  query,
}: {
  query: UseQueryResult<ReconciliationItem[], Error>;
}) {
  const columns: Column<ReconciliationItem>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (r) => (
        <Pill variant={reconciliationItemTone(r.itemType)} dot>
          {reconciliationItemLabel(r.itemType)}
        </Pill>
      ),
    },
    {
      key: 'who',
      header: 'Who',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>
          {r.workerLabel ?? r.clientLabel ?? 'Subject not named'}
        </span>
      ),
    },
    {
      key: 'eventMin',
      header: 'Event min',
      render: (r) => (r.eventMinutes !== null ? String(r.eventMinutes) : '—'),
      numeric: true,
    },
    {
      key: 'sessionMin',
      header: 'Time min',
      render: (r) => (r.sessionMinutes !== null ? String(r.sessionMinutes) : '—'),
      numeric: true,
    },
    {
      key: 'mismatch',
      header: 'Δ min',
      render: (r) => (r.mismatchMinutes !== null ? String(r.mismatchMinutes) : '—'),
      numeric: true,
    },
  ];
  return (
    <DrilldownTable
      query={query}
      columns={columns}
      rowKey={(r) => `${r.itemType}:${r.serviceEventId ?? r.timeSessionId ?? '?'}`}
      emptyHeading="Everything lines up"
      emptyMessage="No reconciliation items to review."
    />
  );
}

// ---------------------------------------------------------------------------
// 5 · Exceptions
// ---------------------------------------------------------------------------

export function ExceptionsWidget({ period }: { period: DashboardPeriod }) {
  const [open, setOpen] = useState(false);

  const summary = useQuery({
    queryKey: ['dashboard', 'exceptions', period],
    queryFn: () => dashboard.exceptions({ period }),
  });

  const details = useQuery({
    queryKey: ['dashboard', 'exceptions', 'detail'],
    queryFn: () => dashboard.exceptionDetails(),
    enabled: open,
  });

  return (
    <WidgetCard
      title="Exceptions"
      open={open}
      onToggle={() => setOpen((v) => !v)}
      summary={<ExceptionSummaryBody query={summary} />}
      drilldown={<ExceptionDrilldown query={details} />}
    />
  );
}

/**
 * WHICH ALERTS THESE FOUR COUNTS DESCRIBE — stated even though the answer is
 * always "all of them".
 *
 * `ExceptionSummary.scope` is documented as always `organization`: `alert`
 * carries no program or site column, so nothing narrows this roll-up and this
 * note renders nothing today. Reading the field anyway is not ceremony. The
 * card's DRILL-DOWN is narrowed by the supervisor edge, so the day a reader's
 * roll-up stops being the organization's — a program column on `alert`, a
 * tenant-scoped alert feed — the summary would be the last of the five cards
 * still framing a subset as everybody, and it would do it silently.
 */
function ExceptionSummaryBody({ query }: { query: UseQueryResult<ExceptionSummary, Error> }) {
  return (
    <SummaryBody
      query={query}
      failedMessage="We couldn’t load exceptions just now, so these are not zeros."
      render={(d) => (
        <>
          <ScopeNote note={queueScopeNote(d.scope, EXCEPTION_CASELOAD)} />
          <StatGrid>
            <Stat label="Active" value={formatCount(d.totalActive)} />
            <Stat label="Critical" value={String(d.bySeverity.critical)} />
            <Stat label="Warning" value={String(d.bySeverity.warning)} />
            <Stat label="Info" value={String(d.bySeverity.info)} />
          </StatGrid>
          <TrendLine
            label={exceptionTrendLabel(d.trend.direction)}
            tone={exceptionTrendTone(d.trend.direction)}
          />
        </>
      )}
    />
  );
}

function ExceptionDrilldown({ query }: { query: UseQueryResult<ExceptionDetail[], Error> }) {
  const columns: Column<ExceptionDetail>[] = [
    {
      key: 'severity',
      header: 'Severity',
      render: (r) => (
        <Pill variant={alertSeverityTone(r.severity)} dot>
          {r.severity}
        </Pill>
      ),
    },
    {
      key: 'title',
      header: 'Alert',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.title}</span>
          {r.alertType ? <span className="rn-muted"> · {r.alertType}</span> : null}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <span>{r.status}</span>,
    },
  ];
  return (
    <DrilldownTable
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      emptyHeading="No active alerts"
      emptyMessage="Nothing needs attention right now."
    />
  );
}
